import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { ValuationAllocationService } from 'api';
import { SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS } from 'common/constants/services';
import {
  OPM_INPUT_SETS_DEFAULT_ERROR_MESSAGE,
  OPM_INPUT_SETS_QUERY_KEY,
} from 'common/constants/services/valuations';
import { throwError } from 'utilities';
import { GetOPMInputSetsHook, GetOPMInputSetsParams, UseGetOPMInputSets } from './types';

/**
 * Distinct OPM input sets across a valuation (OPM scenarios, Backsolve, Future Exit and saved
 * OPM Calculators), rounded and de-duplicated by the backend. One set is one OPM calculations
 * PDF page.
 */
export const getOPMInputSets = async (params: GetOPMInputSetsParams) => {
  const { allocationId } = params;

  if (isNil(allocationId)) {
    return null;
  }

  try {
    const response = await ValuationAllocationService.apiValuationAllocationOpmInputSetsRead(toString(allocationId));
    return response.input_sets;
  } catch (error) {
    return throwError(OPM_INPUT_SETS_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetOPMInputSets: UseGetOPMInputSets = params => {
  const { allocationId, shouldQueryAutomatically = true } = params;

  const getInputSets = useCallback(() => getOPMInputSets({ allocationId }), [allocationId]);

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<GetOPMInputSetsHook['data'], Error>({
    enabled: !isNil(allocationId) && shouldQueryAutomatically,
    queryFn: getInputSets,
    queryKey: [OPM_INPUT_SETS_QUERY_KEY, allocationId],
    refetchOnWindowFocus: SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetOPMInputSets;
